import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useNavigation } from '../../utilities/NavigationContext';
import { useAuth } from '../../utilities/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

class UpdatePassword extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      password: '',
      passwordConfirm: '',
      token: '',
      loading: false,
      passwordMatch: true,
      tokenChecked: false,
    };
  }

  componentDidMount() {
    const currentUrl = window.location.href;
    const urlParams = new URLSearchParams(new URL(currentUrl).search);

    if (urlParams.has('token')) {
      const token = urlParams.get('token');
      this.setState({
        token,
        tokenChecked: true,
      });
    } else {
      toast.error('This link is invalid. Check your email again please', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
        hideProgressBar: false,
      });

      this.props.navigate('/login');
    }
  }

  handleInputChange = (event) => {
    const { name, value } = event.target;

    if (name === 'password' || name === 'passwordConfirm') {
      const password =
        name === 'password' ? value : this.state.password;
      const passwordConfirm =
        name === 'passwordConfirm' ? value : this.state.passwordConfirm;

      this.setState({
        [name]: value,
        passwordMatch: password === passwordConfirm,
      });
      return;
    }

    this.setState({ [name]: value });
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    const { password, passwordConfirm, token, loading } = this.state;
    if (loading) return;

    if (!token) {
      toast.error('Reset token is missing. Please use the link from your email.', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2500,
        hideProgressBar: false,
      });
      return;
    }

    if (!password || !passwordConfirm) {
      toast.error('Please enter and confirm your new password.', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
        hideProgressBar: false,
      });
      return;
    }

    if (password !== passwordConfirm) {
      toast.error('Passwords do not match!', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
        hideProgressBar: false,
      });
      return;
    }

    this.setState({ loading: true });

    try {
      const response = await axios.post(`${EndPoint}/user/update-password`, {
        password,
        token,
      });

      if (response.status === 200) {
        toast.success(response.data.message || 'Password updated successfully.', {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
          hideProgressBar: false,
        });

        this.setState({ loading: false });
        this.props.navigate('/login');
      } else {
        toast.error('Unable to update password.', {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
          hideProgressBar: false,
        });
        this.setState({ loading: false });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Error updating password.',
        {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
          hideProgressBar: false,
        }
      );
      this.setState({ loading: false });
    }
  };

  render() {
    const {
      password,
      passwordConfirm,
      loading,
      passwordMatch,
      tokenChecked,
    } = this.state;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-sky-950 to-cyan-900">
        <ToastContainer />

        <div className="mx-auto flex min-h-screen max-w-7xl items-center px-4 py-10">
          <div className="grid w-full overflow-hidden rounded-[32px] border border-white/10 bg-white/10 shadow-2xl backdrop-blur-xl lg:grid-cols-2">
            {/* Left panel */}
            <div className="relative hidden overflow-hidden bg-gradient-to-br from-sky-900 via-cyan-800 to-slate-900 p-10 lg:flex lg:flex-col lg:justify-between">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(56,189,248,0.2),transparent_30%)]" />

              <div className="relative z-10">
                <div className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-100">
                  Caredevo
                </div>

                <h1 className="mt-6 text-4xl font-bold leading-tight text-white">
                  Create a new password
                </h1>

                <p className="mt-4 max-w-md text-sm leading-7 text-sky-100/90">
                  Choose a secure new password for your account, then return to
                  login and continue using your Caredevo workspace.
                </p>
              </div>

              <div className="relative z-10 grid gap-4">
                <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-sm font-semibold text-white">
                    Secure reset
                  </p>
                  <p className="mt-1 text-sm text-sky-100/80">
                    Your reset link includes a token so only the intended user
                    can update the password.
                  </p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-sm font-semibold text-white">
                    Quick recovery
                  </p>
                  <p className="mt-1 text-sm text-sky-100/80">
                    Set a new password and get back into your account in just a
                    few steps.
                  </p>
                </div>
              </div>
            </div>

            {/* Right panel */}
            <div className="bg-white px-6 py-10 sm:px-10 lg:px-12">
              <div className="mx-auto w-full max-w-md">
                <div className="mb-8 lg:hidden">
                  <div className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
                    Caredevo
                  </div>
                </div>

                <h2 className="text-3xl font-bold tracking-tight text-slate-900">
                  Reset Password
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  Enter your new password below to update your account access.
                </p>

                {tokenChecked && (
                  <div className="mt-6 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
                    Your reset link has been recognised. You can now set a new
                    password.
                  </div>
                )}

                <form onSubmit={this.handleSubmit} className="mt-8 space-y-5">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      New Password
                    </label>
                    <input
                      type="password"
                      name="password"
                      placeholder="Enter new password"
                      value={password}
                      onChange={this.handleInputChange}
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      name="passwordConfirm"
                      placeholder="Confirm new password"
                      value={passwordConfirm}
                      onChange={this.handleInputChange}
                      className={`w-full rounded-2xl px-4 py-3 text-sm text-slate-900 outline-none transition focus:bg-white focus:ring-4 ${
                        passwordConfirm && !passwordMatch
                          ? 'border border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100'
                          : 'border border-slate-200 bg-slate-50 focus:border-sky-400 focus:ring-sky-100'
                      }`}
                    />
                    {passwordConfirm && !passwordMatch && (
                      <p className="mt-2 text-xs font-medium text-red-600">
                        Passwords do not match.
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full rounded-2xl px-4 py-3 text-sm font-semibold text-white shadow-sm transition ${
                      loading
                        ? 'cursor-not-allowed bg-slate-400'
                        : 'bg-sky-700 hover:bg-sky-800'
                    }`}
                  >
                    {loading ? 'Updating...' : 'Reset Password'}
                  </button>
                </form>

                <div className="mt-8 border-t border-slate-200 pt-6 text-center text-sm text-slate-500">
                  Remember your password?{' '}
                  <button
                    type="button"
                    onClick={() => this.props.navigate('/login')}
                    className="font-semibold text-sky-700 transition hover:text-sky-900"
                  >
                    Login here
                  </button>
                </div>

                <div className="mt-2 text-center text-sm text-slate-500">
                  Don&apos;t have an account?{' '}
                  <Link
                    to="/register"
                    className="font-semibold text-sky-700 transition hover:text-sky-900"
                  >
                    Register here
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const UpdatePasswordWithNavigation = (props) => {
  const navigate = useNavigation();
  const [, setAuth] = useAuth();

  return (
    <UpdatePassword
      {...props}
      navigate={navigate}
      setAuth={setAuth}
    />
  );
};

export default UpdatePasswordWithNavigation;